import { formatDate } from '@/lib/utils/timezone';
import { OneOnOneMeeting } from '@/lib/types';
import { Calendar, Clock, Users } from 'lucide-react';
import { useState, useContext } from 'react';
import { MeetingDetails } from './meeting-details';
import { Button } from '@/components/ui/button';
import { AppContext } from '@/lib/app-context';

interface MeetingListProps {
  meetings: OneOnOneMeeting[];
}

export function MeetingList({ meetings }: MeetingListProps) {
  const [selectedMeeting, setSelectedMeeting] = useState<OneOnOneMeeting | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { setCurrentPage } = useContext(AppContext);

  const handleMeetingClick = (meeting: OneOnOneMeeting) => {
    setSelectedMeeting(meeting);
    setIsOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
      setTimeout(() => setSelectedMeeting(null), 300);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Meetings</h2>
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="text-blue-500 hover:text-blue-400"
            onClick={() => setCurrentPage('completed')}
          >
            View Completed Meetings
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {meetings.map((meeting) => (
          <div
            key={meeting.id}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
            onClick={() => handleMeetingClick(meeting)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                <span className="font-medium">
                  {formatDate(meeting.meeting_date)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>{meeting.duration_minutes} minutes</span>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div className="text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Team Member: </span>
                    <span className="font-medium">{meeting.team_member?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Facilitator: </span>
                    <span className="font-medium">{meeting.facilitator?.name}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  meeting.status === 'scheduled'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : meeting.status === 'completed'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300'
                }`}
              >
                {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <MeetingDetails
        meeting={selectedMeeting || meetings[0]}
        open={isOpen}
        onOpenChange={handleOpenChange}
      />
    </>
  );
}
